import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import AuthImage from "@/components/AuthImage";
import generateReceivingPdf, { STATUS_LABEL, CONDITION_LABEL, ISSUE_LABEL } from "@/lib/inventoryPdf";
import { ArrowLeft, CheckCircle2, FileDown, PenLine, AlertTriangle, Ban, Truck, Trash2, X, ShieldAlert } from "lucide-react";

const SHIPMENT_TYPE_LABEL = { pallets: "Pallets", boxes: "Boxes", pallets_and_boxes: "Pallets + Boxes" };
const DELETE_REASONS = [
  "Duplicate Receiving Record",
  "Incorrect Shipment Entry",
  "Test Entry",
  "Shipment Entered by Mistake",
  "Other",
];

const STATUS_CLS = {
  accepted: "bg-emerald-500/10 text-emerald-300 border-emerald-400/40",
  accepted_with_issues: "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40",
  cancelled: "bg-red-500/10 text-red-300 border-red-400/40",
  draft: "bg-slate-500/10 text-slate-300 border-slate-400/40",
  counting: "bg-blue-500/10 text-blue-300 border-blue-400/40",
  review: "bg-purple-500/10 text-purple-300 border-purple-400/40",
};

export default function ReceivingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [amending, setAmending] = useState(false);
  const [amendReason, setAmendReason] = useState("");
  const [amendField, setAmendField] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState(DELETE_REASONS[0]);
  const [deleteComment, setDeleteComment] = useState("");

  const load = useCallback(() => {
    api.get(`/inventory_receivings/${id}`).then(({ data }) => setRec(data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (!rec) return <div className="text-[color:var(--text-secondary)]">Loading…</div>;

  const download = async () => {
    setDownloading(true);
    try {
      await generateReceivingPdf(rec);
      api.post(`/inventory_receivings/${rec.id}/downloaded`).catch(() => {});
      toast.success("PDF downloaded");
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setDownloading(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancel this draft receiving? This cannot be undone.")) return;
    try {
      await api.post(`/inventory_receivings/${rec.id}/cancel`);
      toast.success("Cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitAmend = async () => {
    if (!amendReason.trim() || !amendField.trim()) return toast.error("Field name and reason are required");
    try {
      await api.post(`/inventory_receivings/${rec.id}/amend`, {
        field: amendField, reason: amendReason,
        original_value: null, corrected_value: null,
      });
      toast.success("Amendment logged");
      setAmending(false); setAmendField(""); setAmendReason(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitDelete = async () => {
    const reason = deleteReason === "Other" ? (deleteComment.trim() || "") : deleteReason;
    if (!reason) return toast.error("Please enter a reason");
    if (deleteReason === "Other" && !deleteComment.trim()) return toast.error("Comments required when reason is Other");
    try {
      await api.post(`/inventory_receivings/${rec.id}/delete`, { reason, comment: deleteComment });
      toast.success("Receiving record deleted");
      nav("/receiving");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const isAccepted = ["accepted", "accepted_with_issues"].includes(rec.status);
  const justAccepted = location.state?.justAccepted;
  const canAmend = isAccepted && hasAccess(user?.role, ["admin", "gm", "manager"]);
  const canDelete = hasAccess(user?.role, ["admin", "gm"]) ||
                    (hasAccess(user?.role, ["manager"]) && rec.received_by_user_id === user?.id);
  const shipmentType = rec.shipment_type || "pallets";
  const showPallets = shipmentType === "pallets" || shipmentType === "pallets_and_boxes";
  const showLoose = shipmentType === "boxes" || shipmentType === "pallets_and_boxes";
  const notePhotos = rec.delivery_note_photos || [];
  const generalPhotos = rec.general_photos || [];

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="receiving-detail-page">
      <button onClick={() => nav("/receiving")} className="text-[color:var(--text-secondary)] flex items-center gap-1 hover:text-[color:var(--gold)]"><ArrowLeft size={14}/> Back to Receiving</button>

      {justAccepted && (
        <div className="hos-card p-5 border border-emerald-400/50 bg-emerald-500/10 text-emerald-300 flex items-center gap-3">
          <CheckCircle2 size={20}/>
          <div>
            <div className="font-heading text-lg">Inventory Received &amp; Accepted</div>
            <div className="text-sm">{rec.reference_number} · {rec.total_pallets} pallets · {rec.total_boxes} boxes · signed by {rec.signed_by_name}</div>
          </div>
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Receiving Reference</div>
          <h1 className="font-heading text-3xl">{rec.reference_number}</h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">{rec.store_name} · {rec.supplier_name || "—"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-[11px] px-2 py-1 rounded-full border ${STATUS_CLS[rec.status]}`}>{STATUS_LABEL[rec.status] || rec.status}</span>
          {isAccepted && (
            <button className="hos-btn-primary flex items-center gap-2 text-sm" onClick={download} disabled={downloading} data-testid="download-pdf-btn">
              <FileDown size={14}/> {downloading ? "Preparing…" : "Download PDF"}
            </button>
          )}
          {!isAccepted && rec.status !== "cancelled" && (
            <>
              <Link to={`/receiving/${rec.id}/edit`} className="hos-btn-outline text-sm">Continue Editing</Link>
              <button className="hos-btn-outline text-sm flex items-center gap-1" onClick={cancel}><Ban size={14}/> Cancel</button>
            </>
          )}
          {canDelete && !rec.is_deleted && (
            <button className="hos-btn-outline text-sm flex items-center gap-1 text-[color:var(--error)] border-[color:var(--error)]/40 hover:bg-[color:var(--error)]/10"
                    onClick={() => setDeleting(true)} data-testid="delete-record-btn">
              <Trash2 size={14}/> Delete
            </button>
          )}
        </div>
      </div>

      {/* Shipment type + totals banner */}
      <div className="hos-card p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniInfo label="Shipment Type" value={SHIPMENT_TYPE_LABEL[shipmentType]}/>
        {showPallets && <MiniInfo label="Pallets" value={rec.total_pallets || 0}/>}
        {showPallets && shipmentType === "pallets_and_boxes" && <MiniInfo label="Boxes on Pallets" value={rec.boxes_on_pallets || 0}/>}
        {showLoose && <MiniInfo label="Loose Boxes" value={rec.loose_boxes || 0}/>}
        <MiniInfo label="Total Boxes" value={rec.total_boxes || 0} accent/>
      </div>

      {/* Shipment details */}
      <div className="hos-card p-6">
        <h3 className="font-heading text-xl mb-3">Shipment Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
          <Info label="Purchase Order" value={rec.purchase_order_number || "—"}/>
          <Info label="Invoice" value={rec.invoice_number || "—"}/>
          <Info label="Arrival" value={rec.arrival_at ? new Date(rec.arrival_at).toLocaleString() : "—"}/>
          <Info label="Carrier" value={rec.carrier_name || "—"}/>
          <Info label="Driver" value={rec.driver_name || "—"}/>
          <Info label="Driver Phone" value={rec.driver_phone || "—"}/>
          <Info label="Truck / Vehicle" value={rec.truck_number || "—"}/>
          <Info label="Trailer" value={rec.trailer_number || "—"}/>
          <Info label="Delivery Ref" value={rec.delivery_reference || "—"}/>
        </div>
      </div>

      {/* Pallet details */}
      {showPallets && (
      <div className="hos-card p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading text-xl">Pallets</h3>
          <div className="flex items-center gap-2">
            <div className="text-right">
              <div className="label-caps">Total</div>
              <div className="font-heading text-2xl text-[color:var(--gold)]">{rec.total_pallets} pallets · {rec.total_boxes} boxes</div>
            </div>
            <Link to={`/damages/new?source=receiving&receiving_id=${rec.id}`} className="hos-btn-outline flex items-center gap-1 text-xs" data-testid="report-shipment-damage-btn">
              <ShieldAlert size={14}/> Report Damage
            </Link>
          </div>
        </div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl">Pallets</h3>
          <div className="text-right">
            <div className="label-caps">Total</div>
            <div className="font-heading text-2xl text-[color:var(--gold)]">{rec.total_pallets} pallets · {rec.total_boxes} boxes</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="text-left label-caps"><tr>
            <th className="py-2">Pallet</th><th>Boxes</th><th>Condition</th><th>Notes</th><th>Photos</th>
          </tr></thead>
          <tbody>
            {(rec.pallets || []).map((p) => (
              <tr key={p.id} className="border-t border-[color:var(--border)] align-top">
                <td className="py-2 font-semibold">
                  <div>Pallet {p.pallet_number}</div>
                  {p.barcode && <div className="text-[10px] font-normal text-[color:var(--text-secondary)] font-mono">⌗ {p.barcode}</div>}
                </td>
                <td>{p.box_count}</td>
                <td>{CONDITION_LABEL[p.condition] || p.condition}</td>
                <td className="text-[color:var(--text-secondary)]">{p.notes || "—"}</td>
                <td>
                  <div className="flex gap-1 flex-wrap">
                    {(p.photos || []).map((ph, i) => (
                      <AuthImage key={i} photo={ph} className="w-12 h-12 object-cover rounded" alt="pallet"/>
                    ))}
                    {!(p.photos || []).length && <span className="text-[color:var(--text-secondary)]">—</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {showLoose && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Loose Boxes</h3>
          <div className="font-heading text-5xl text-[color:var(--gold)]">{rec.loose_boxes || 0}</div>
          <div className="text-sm text-[color:var(--text-secondary)] mt-1">boxes received without a pallet</div>
        </div>
      )}

      {(rec.issues || []).length > 0 && (
        <div className="hos-card p-6">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--warning)]">
            <AlertTriangle size={16}/><h3 className="font-heading text-xl">Delivery Issues ({rec.issues.length})</h3>
          </div>
          <ul className="space-y-3">
            {rec.issues.map((it) => {
              const pallet = (rec.pallets || []).find((p) => p.id === it.pallet_id);
              return (
                <li key={it.id} className="border-t border-[color:var(--border)] pt-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold">{ISSUE_LABEL[it.issue_type] || it.issue_type}</span>
                    {pallet && <span className="text-xs text-[color:var(--text-secondary)]">· Pallet {pallet.pallet_number}</span>}
                    {it.quantity_affected ? <span className="text-xs text-[color:var(--text-secondary)]">· {it.quantity_affected} affected</span> : null}
                  </div>
                  {it.description && <div className="text-sm text-[color:var(--text-secondary)] mt-1">{it.description}</div>}
                  {(it.photos || []).length > 0 && (
                    <div className="flex gap-1 flex-wrap mt-2">
                      {it.photos.map((p, i) => <AuthImage key={i} photo={p} className="w-14 h-14 object-cover rounded"/>)}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {notePhotos.length > 0 && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Delivery Note</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {notePhotos.map((p, i) => (
              <div key={i} className="relative aspect-square">
                <AuthImage photo={p} className="w-full h-full object-cover rounded"/>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1 py-0.5">Page {i + 1}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {generalPhotos.length > 0 && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Shipment Photos</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {generalPhotos.map((p, i) => (
              <AuthImage key={i} photo={p} className="aspect-square w-full object-cover rounded"/>
            ))}
          </div>
        </div>
      )}

      {isAccepted && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Acceptance</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <Info label="Received &amp; Accepted By" value={rec.signed_by_name || "—"}/>
            <Info label="Signed At" value={rec.signed_at ? new Date(rec.signed_at).toLocaleString() : "—"}/>
            <Info label="Electronic Signature" value={`Signed by ${rec.signed_by_name}`}/>
          </div>
        </div>
      )}

      {(rec.amendments || []).length > 0 && (
        <div className="hos-card p-6">
          <h3 className="font-heading text-xl mb-3">Amendments ({rec.amendments.length})</h3>
          <ul className="space-y-2 text-sm">
            {rec.amendments.map((a) => (
              <li key={a.id} className="border-t border-[color:var(--border)] pt-2">
                <div className="font-semibold">{a.field}</div>
                <div className="text-xs text-[color:var(--text-secondary)]">by {a.user_name} · {new Date(a.at).toLocaleString()}</div>
                <div className="text-sm mt-1">{a.reason}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {canAmend && (
        <div className="hos-card p-6">
          {!amending ? (
            <button className="hos-btn-outline flex items-center gap-2 text-sm" onClick={() => setAmending(true)} data-testid="amend-btn">
              <PenLine size={14}/> Add Amendment
            </button>
          ) : (
            <div className="space-y-2">
              <div className="label-caps">Add Amendment</div>
              <input className="hos-input" placeholder="Field affected (e.g., Pallet 4 box count)" value={amendField} onChange={(e) => setAmendField(e.target.value)}/>
              <textarea rows={3} className="hos-input" placeholder="Reason (e.g., Pallet 4 box count incorrectly entered as 21 instead of 22)" value={amendReason} onChange={(e) => setAmendReason(e.target.value)}/>
              <div className="flex gap-2 justify-end">
                <button className="hos-btn-outline" onClick={() => { setAmending(false); setAmendField(""); setAmendReason(""); }}>Cancel</button>
                <button className="hos-btn-primary" onClick={submitAmend} data-testid="amend-save-btn">Save Amendment</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleting && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="hos-card w-full max-w-md p-6" data-testid="delete-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-2xl flex items-center gap-2 text-[color:var(--error)]"><Trash2 size={20}/> Delete Shipment Record?</h3>
              <button onClick={() => setDeleting(false)} className="p-1"><X size={16}/></button>
            </div>
            <div className="space-y-1 text-sm mb-4">
              <div><span className="label-caps text-[10px]">Reference </span><b>{rec.reference_number}</b></div>
              <div><span className="label-caps text-[10px]">Supplier </span>{rec.supplier_name || "—"}</div>
              <div><span className="label-caps text-[10px]">PO </span>{rec.purchase_order_number || "—"}</div>
              <div><span className="label-caps text-[10px]">Received </span>{rec.arrival_at ? new Date(rec.arrival_at).toLocaleString() : "—"}</div>
            </div>
            <div className="p-3 bg-[color:var(--warning)]/10 border border-[color:var(--warning)]/40 rounded-md text-xs text-[color:var(--warning)] mb-4">
              This will remove this shipment from Receiving History. The audit log will retain a record of who deleted it and why.
            </div>
            <label className="label-caps">Reason for Deletion</label>
            <select className="hos-input mt-1" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} data-testid="delete-reason-select">
              {DELETE_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            {deleteReason === "Other" && (
              <textarea rows={2} className="hos-input mt-2" placeholder="Please explain…" value={deleteComment} onChange={(e) => setDeleteComment(e.target.value)} data-testid="delete-comment"/>
            )}
            {deleteReason !== "Other" && (
              <input className="hos-input mt-2" placeholder="Optional comments" value={deleteComment} onChange={(e) => setDeleteComment(e.target.value)}/>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button className="hos-btn-outline" onClick={() => setDeleting(false)} data-testid="delete-cancel">Cancel</button>
              <button className="hos-btn-primary bg-[color:var(--error)] hover:bg-[color:var(--error)]/90" onClick={submitDelete} data-testid="delete-confirm">Delete Record</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="label-caps">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

function MiniInfo({ label, value, accent }) {
  return (
    <div className="rounded-md border border-[color:var(--border)] p-3">
      <div className="label-caps text-[10px]">{label}</div>
      <div className={`font-heading text-2xl ${accent ? "text-[color:var(--gold)]" : ""}`}>{value ?? 0}</div>
    </div>
  );
}
